import {
  type ChatMessage,
  ChatRole,
  type ChatToolResultContent,
  isChatToolResultContent,
  isChatToolUseContent,
} from "@/shared/domain/chat/ChatRecord";

export interface MessageSegment {
  type: "messages";
  messages: ChatMessage[];
  messageIndices: number[];
}

export interface ToolGroupSegment {
  type: "toolGroup";
  toolNames: string[];
  toolResults: ChatToolResultContent[];
  totalRuntimeMs: number;
  messageIndices: number[];
}

export type MessageGroup = MessageSegment | ToolGroupSegment;

const hasToolUseContent = (message: ChatMessage): boolean => {
  return (
    message.role === ChatRole.ASSISTANT &&
    message.content.some(isChatToolUseContent)
  );
};

const hasToolResultContent = (message: ChatMessage): boolean => {
  return (
    message.role === ChatRole.ROBOTO &&
    message.content.some(isChatToolResultContent)
  );
};

const getNonToolUseMessage = (message: ChatMessage): ChatMessage | null => {
  const content = message.content.filter(
    (contentItem) => !isChatToolUseContent(contentItem),
  );

  if (content.length === 0) {
    return null;
  }

  return {
    ...message,
    content,
  };
};

const createToolGroupAtIndex = (
  messages: ChatMessage[],
  startIndex: number,
): {
  nextIndex: number;
  leadingSegments: MessageSegment[];
  segment: ToolGroupSegment;
} | null => {
  const leadingSegments: MessageSegment[] = [];
  const messageIndices: number[] = [];
  const toolResults: ChatToolResultContent[] = [];

  let nextIndex = startIndex;
  let isFirstPair = true;

  while (
    nextIndex + 1 < messages.length &&
    hasToolUseContent(messages[nextIndex]) &&
    hasToolResultContent(messages[nextIndex + 1])
  ) {
    if (!isFirstPair && getNonToolUseMessage(messages[nextIndex]) !== null) {
      break;
    }

    const assistantDisplayMessage = getNonToolUseMessage(messages[nextIndex]);

    if (assistantDisplayMessage) {
      leadingSegments.push({
        type: "messages",
        messages: [assistantDisplayMessage],
        messageIndices: [nextIndex],
      });
    }

    messageIndices.push(nextIndex, nextIndex + 1);
    toolResults.push(
      ...messages[nextIndex + 1].content.filter(isChatToolResultContent),
    );
    nextIndex += 2;
    isFirstPair = false;
  }

  if (toolResults.length < 1) {
    return null;
  }

  return {
    leadingSegments,
    nextIndex,
    segment: {
      type: "toolGroup",
      toolNames: toolResults.map((toolResult) => toolResult.tool_name),
      toolResults,
      totalRuntimeMs: toolResults.reduce(
        (total, toolResult) => total + toolResult.runtime_ms,
        0,
      ),
      messageIndices,
    },
  };
};

export const groupToolMessages = (messages: ChatMessage[]): MessageGroup[] => {
  const segments: MessageGroup[] = [];
  const pendingMessages: ChatMessage[] = [];
  const pendingIndices: number[] = [];

  const flushPendingMessages = () => {
    if (pendingMessages.length === 0) {
      return;
    }

    segments.push({
      type: "messages",
      messages: [...pendingMessages],
      messageIndices: [...pendingIndices],
    });

    pendingMessages.length = 0;
    pendingIndices.length = 0;
  };

  let index = 0;

  while (index < messages.length) {
    const toolGroup = createToolGroupAtIndex(messages, index);

    if (toolGroup) {
      flushPendingMessages();
      segments.push(...toolGroup.leadingSegments);
      segments.push(toolGroup.segment);
      index = toolGroup.nextIndex;
      continue;
    }

    pendingMessages.push(messages[index]);
    pendingIndices.push(index);
    index += 1;
  }

  flushPendingMessages();

  return segments;
};
